// Avaliador de regras de campo de integração em JS — espelha
// app/Services/Integracao/CampoIntegracao/RegraCampoIntegracaoEvaluator.php
// O resultado inclui um trace com qual caso casou para alimentar a UI de teste.

import { aplicarFuncoes } from "../aplicarFuncoes";

const OPERADORES_VALIDOS = [
  "igual", "diferente",
  "maior", "maior_igual", "menor", "menor_igual",
  "entre",
  "em", "nao_em",
  "contem", "nao_contem",
  "comeca_com", "termina_com",
  "nulo", "nao_nulo",
];

const ALIASES_OPERADOR = {
  "==": "igual",
  "!=": "diferente",
  "<>": "diferente",
  ">": "maior",
  ">=": "maior_igual",
  "<": "menor",
  "<=": "menor_igual",
  in: "em",
  not_in: "nao_em",
  contains: "contem",
  starts_with: "comeca_com",
  ends_with: "termina_com",
  is_null: "nulo",
  is_not_null: "nao_nulo",
};

function dataGet(obj, path) {
  if (obj == null || !path) return null;
  const partes = String(path).split(".");
  let atual = obj;
  for (const p of partes) {
    if (atual == null) return null;
    if (typeof atual !== "object") return null;
    atual = atual[p];
  }
  return atual === undefined ? null : atual;
}

function soft(v) {
  if (typeof v === "boolean") return v ? 1 : 0;
  return v;
}

// Coerção branda só quando um dos lados é booleano — espelha o `softEqual` PHP.
// Sem isso, `null == 0` ou `"" == 0` retornavam true em JS (loose equality)
// mas false em PHP, causando divergência entre o testador e o runtime.
function softEqual(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return soft(a) == soft(b); // eslint-disable-line eqeqeq
  }
  return a === b;
}

// Para os operadores `em`/`nao_em`: aceita array nativo OR string JSON.
// Lança erro se não for parseável (espelha o evaluator PHP).
function coerceArray(valor, op) {
  if (Array.isArray(valor)) return valor;
  if (typeof valor === "string") {
    try {
      const decoded = JSON.parse(valor);
      if (Array.isArray(decoded)) return decoded;
    } catch (_) { /* falha abaixo */ }
  }
  throw new Error(`Operador '${op}' requer array, recebido: ${typeof valor}`);
}

function canonicalOperador(op) {
  const norm = String(op || "").trim().toLowerCase();
  const canonical = ALIASES_OPERADOR[norm] || norm;
  if (!OPERADORES_VALIDOS.includes(canonical)) {
    throw new Error(`Operador inválido: ${op}`);
  }
  return canonical;
}

function comparar(a, op, b) {
  switch (op) {
    case "igual":       return softEqual(a, b);
    case "diferente":   return !softEqual(a, b);
    case "maior":       return a !== null && b !== null && soft(a) > soft(b);
    case "maior_igual": return a !== null && b !== null && soft(a) >= soft(b);
    case "menor":       return a !== null && b !== null && soft(a) < soft(b);
    case "menor_igual": return a !== null && b !== null && soft(a) <= soft(b);
    case "entre":
      if (!Array.isArray(b) || b.length < 2 || a === null) return false;
      if (Number.isNaN(Number(a)) || Number.isNaN(Number(b[0])) || Number.isNaN(Number(b[1]))) return false;
      return Number(a) >= Number(b[0]) && Number(a) <= Number(b[1]);
    case "em":          return coerceArray(b, "em").includes(a);
    case "nao_em":      return !coerceArray(b, "nao_em").includes(a);
    case "contem":      return a !== null && b !== null && String(a).includes(String(b));
    case "nao_contem":  return a === null || b === null || !String(a).includes(String(b));
    case "comeca_com":  return a !== null && b !== null && String(a).startsWith(String(b));
    case "termina_com": return a !== null && b !== null && String(a).endsWith(String(b));
    case "nulo":        return a === null || a === "";
    case "nao_nulo":    return a !== null && a !== "";
    default:
      throw new Error(`Operador inválido: ${op}`);
  }
}

function avaliarCondicao(objeto, cond) {
  const operador = canonicalOperador(cond.operador || "igual");
  const tipoValor = cond.tipo_valor || "static";
  const valorEsq = cond.campo ? dataGet(objeto, cond.campo) : null;
  const valorDir = tipoValor === "campo" ? dataGet(objeto, cond.valor) : cond.valor;
  return comparar(valorEsq, operador, valorDir);
}

function avaliarGrupo(objeto, grupo) {
  const logica = String(grupo.logica || "AND").toUpperCase();
  const condicoes = grupo.condicoes || [];
  if (condicoes.length === 0) return true;
  for (const cond of condicoes) {
    const r = avaliarCondicao(objeto, cond);
    if (logica === "OR" && r) return true;
    if (logica === "AND" && !r) return false;
  }
  return logica === "AND";
}

function avaliarBlocoCondicao(objeto, bloco) {
  const grupos = (bloco && bloco.grupos) || [];
  if (grupos.length === 0) return true;
  for (const g of grupos) {
    if (!avaliarGrupo(objeto, g)) return false;
  }
  return true;
}

function isMultiSaida(saida) {
  if (!Array.isArray(saida) || saida.length === 0) return false;
  const primeiro = saida[0];
  return primeiro && typeof primeiro === "object" && "expressao" in primeiro;
}

function resolverExpressao(objeto, expr) {
  if (expr == null) return null;
  if (typeof expr !== "object") return expr;
  const tipo = expr.tipo || "literal";
  let valor;
  if (tipo === "literal") valor = expr.valor == null ? null : expr.valor;
  else if (tipo === "campo") valor = dataGet(objeto, expr.caminho || "");
  else throw new Error(`Tipo de expressão inválido: ${tipo}`);
  return aplicarFuncoes(valor, expr.funcoes);
}

function resolverSaida(objeto, saida) {
  if (isMultiSaida(saida)) {
    return saida.map((item) => ({
      para: item.para && item.para !== "" ? String(item.para) : null,
      valor: resolverExpressao(objeto, item.expressao),
    }));
  }
  return resolverExpressao(objeto, saida);
}

function avaliarCondicional(objeto, regra) {
  const casos = regra.casos || [];
  for (let i = 0; i < casos.length; i++) {
    if (avaliarBlocoCondicao(objeto, casos[i].condicao || {})) {
      return { matched: { tipo: "caso", index: i }, valor: resolverSaida(objeto, casos[i].entao) };
    }
  }
  return {
    matched: { tipo: regra.senao != null ? "senao" : "nenhum" },
    valor: resolverSaida(objeto, regra.senao),
  };
}

function avaliarCoalesce(objeto, regra) {
  const campos = regra.campos || [];
  for (let i = 0; i < campos.length; i++) {
    const v = dataGet(objeto, campos[i]);
    if (v !== null && v !== "") {
      return { matched: { tipo: "campo", index: i, caminho: campos[i] }, valor: v };
    }
  }
  return { matched: { tipo: "nenhum" }, valor: null };
}

function avaliarConcatenacao(objeto, regra) {
  const sep = regra.separador || "";
  const partes = [];
  for (const expr of regra.partes || []) {
    let v = resolverExpressao(objeto, expr);
    if (v === null || v === "") {
      if (regra.ignorar_vazios) continue;
      v = "";
    }
    partes.push(String(v));
  }
  if (partes.length === 0) return { matched: { tipo: "vazio" }, valor: null };
  return { matched: { tipo: "concat", partes: partes.length }, valor: partes.join(sep) };
}

function normalizarChave(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "boolean") return v ? "1" : "0";
  return String(v);
}

function avaliarMapeamento(objeto, regra) {
  if (!regra.campo) {
    return { matched: { tipo: "padrao" }, valor: regra.padrao == null ? null : regra.padrao };
  }
  const valor = dataGet(objeto, regra.campo);
  const mapa = regra.mapa || {};
  const chave = normalizarChave(valor);
  if (chave !== null && Object.prototype.hasOwnProperty.call(mapa, chave)) {
    return { matched: { tipo: "mapeado", chave, valor }, valor: mapa[chave] };
  }
  return { matched: { tipo: "padrao", chave, valor }, valor: regra.padrao == null ? null : regra.padrao };
}

/**
 * Avalia uma regra com o objeto dado. Retorna { matched, valor } onde:
 * - matched: descreve qual ramo casou (qual caso, fallback, mapeamento, etc.)
 * - valor: resultado bruto (escalar ou array de {para, valor} no multi-saída)
 */
export function avaliarRegra(objeto, regra) {
  if (!regra || !regra.tipo) return { matched: { tipo: "vazio" }, valor: null };
  try {
    switch (regra.tipo) {
      case "condicional":  return avaliarCondicional(objeto, regra);
      case "coalesce":     return avaliarCoalesce(objeto, regra);
      case "concatenacao": return avaliarConcatenacao(objeto, regra);
      case "mapeamento":   return avaliarMapeamento(objeto, regra);
      default:
        return { matched: { tipo: "erro", erro: `Tipo desconhecido: ${regra.tipo}` }, valor: null };
    }
  } catch (e) {
    return { matched: { tipo: "erro", erro: e.message }, valor: null };
  }
}

export { dataGet };
